/**
 * useWebPush - this device's web push subscription (ported from web_admin).
 *
 * Parameterised by the service worker path and the endpoints. The flow:
 *   1. register the `swPath` service worker,
 *   2. read the public VAPID key (`vapidPath`),
 *   3. `subscribe()` -> `pushManager.subscribe` + POST `subscribePath`,
 *   4. `unsubscribe()` -> `sub.unsubscribe()` + DELETE `subscribePath?endpoint=`.
 *
 * The VAPID keys are platform-wide (`webpush.*`); the endpoints are the same
 * ones the admin app uses.
 *
 * A subscription is per DEVICE, which is why this is not part of the account
 * notification matrix: that says which categories may reach you on the push
 * channel, and this says whether this browser can receive push at all.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export interface UseWebPushOptions {
  /** The service worker path under public/, e.g. "/push-sw.js". */
  swPath: string;
  /** The public VAPID key endpoint, relative to the API base. */
  vapidPath?: string;
  /** The subscribe/unsubscribe endpoint, relative to the API base. */
  subscribePath?: string;
}

export interface WebPushApi {
  /** The browser has both the service worker and the push API. */
  supported: boolean;
  /** VAPID is configured on the platform (a public key came back). */
  vapidAvailable: boolean;
  /** This device is subscribed. */
  subscribed: boolean;
  /** An operation is in flight. */
  busy: boolean;
  /** True when the subscription succeeded: permission granted, subscribed, posted. */
  subscribe: () => Promise<boolean>;
  unsubscribe: () => Promise<void>;
}

export function useWebPush(opts: UseWebPushOptions): WebPushApi {
  const { swPath, vapidPath = "/webpush/vapid-public-key", subscribePath = "/webpush/subscribe" } = opts;
  const supported =
    typeof navigator !== "undefined" && "serviceWorker" in navigator && typeof window !== "undefined" && "PushManager" in window;
  const [vapidKey, setVapidKey] = useState<string | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    let alive = true;
    void api
      .get<{ publicKey: string | null }>(vapidPath)
      .then((r) => { if (alive) setVapidKey(r.publicKey); })
      .catch(() => {});
    void navigator.serviceWorker
      .getRegistration(swPath)
      .then((reg) => reg?.pushManager.getSubscription())
      .then((sub) => { if (alive) setSubscribed(!!sub); })
      .catch(() => {});
    return () => { alive = false; };
  }, [supported, vapidPath, swPath]);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!supported || !vapidKey) return false;
    setBusy(true);
    try {
      if (Notification.permission !== "granted") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") return false;
      }
      const reg = await navigator.serviceWorker.register(swPath);
      await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
      await api.post(subscribePath, {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
        userAgent: navigator.userAgent,
      });
      setSubscribed(true);
      return true;
    } catch {
      return false;
    } finally {
      setBusy(false);
    }
  }, [supported, vapidKey, swPath, subscribePath]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration(swPath);
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe().catch(() => {});
        await api.del(`${subscribePath}?endpoint=${encodeURIComponent(endpoint)}`).catch(() => {});
      }
      setSubscribed(false);
    } finally {
      setBusy(false);
    }
  }, [supported, swPath, subscribePath]);

  return { supported, vapidAvailable: !!vapidKey, subscribed, busy, subscribe, unsubscribe };
}
